import { categorize } from '@polaris/core/cosmos'
import { ChainType } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { UserRejectedRequestError } from '@polaris/core/utils'
import type { Wallet } from '@quirks/core'
import {
  useChain,
  useConfig,
  useConnect as useCosmosConnect,
  useQuirks,
} from '@quirks/react'
import { useAtomValue } from 'jotai'
import { useRouter } from 'next/navigation'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import type {
  EcosystemWalletOptions,
  WalletOption,
  WalletOptionsProps,
} from '@/src/hooks/wallet/use-wallet-options'

export const useCosmosWalletOptions = ({
  searchWalletQuery,
  reportConnectingWalletInfo,
}: WalletOptionsProps = {}) => {
  const {
    wallet,
    connect: connectCosmos,
    disconnect,
    setupStatus,
    disconnected,
    rejected,
  } = useCosmosConnect()
  const { ampli } = useAmplitudeAnalytics()
  const { wallets: allCosmosWallets } = useSelectableCosmosWallets({
    searchWalletQuery: searchWalletQuery ?? '',
  })
  const featureFlags = useAtomValue(featureFlagsAtom)

  // Use the address to allow the account name to be a function of the current address
  // to avoid race conditions with account names being associated with the wrong account
  const { address } = useChain('osmosis')
  const [accountNameMap, setAccountNameMap] = useState<Record<string, string>>(
    {},
  )

  // Get current account name from the map using the current address
  const accountName = address ? accountNameMap[address] : undefined

  const [isReady, setIsReady] = useState(false)

  // Add timeout effect to resolve to ready after 2 seconds
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsReady(true)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [])

  // Effect to get account name when wallet or address changes
  useEffect(() => {
    if (wallet?.options.supported_chains.length && address) {
      setIsReady(false)
      wallet
        ?.getAccount('osmosis-1')
        .then((account) => {
          if (account.name) {
            setAccountNameMap((prev) => ({
              ...prev,
              [address as string]: account.name,
            }))
          }
        })
        .finally(() => {
          setIsReady(true)
        })
    }
  }, [wallet, address])

  // Effect to disconnect OKX wallet if connected
  // OKX is known to have issues executing routes, see: https://linear.app/osmosis/issue/POLFE-285/filter-okx-from-cosmosusewalletoptions-and-remove-existing-connections
  useEffect(() => {
    if (wallet?.options.pretty_name.includes('OKX')) {
      disconnect()
    }
  }, [wallet, disconnect])

  const connect = useCallback(
    async ({
      wallet,
      onRetry,
      logEvent = true,
    }: {
      wallet: WalletOption
      onRetry?: () => Promise<void>
      logEvent?: boolean
    }) => {
      const baseInfo = {
        icon: wallet.logoUrl,
        name: wallet.displayName,
        type: ChainType.Cosmos,
      }
      try {
        reportConnectingWalletInfo?.({
          ...baseInfo,
          status: 'connecting',
        })
        await connectCosmos(wallet.id).catch(categorize)
        if (logEvent && ampli) {
          ampli.walletConnectWalletCompleted({
            wallet: wallet.displayName,
            type: 'cosmos',
          })
        }
      } catch (e) {
        const error = e as Error

        reportConnectingWalletInfo?.({
          ...baseInfo,
          status: 'error',
          error: {
            type: (() => {
              if (error instanceof UserRejectedRequestError) {
                return 'rejected'
              }

              return 'unexpected'
            })(),
            onRetry: () => {
              reportConnectingWalletInfo?.({
                ...baseInfo,
                status: 'connecting',
              })
              if (onRetry) {
                return onRetry()
              }

              return connect({ wallet })
            },
          },
        })

        throw error
      }
    },
    [ampli, connectCosmos, reportConnectingWalletInfo],
  )

  const connectableOptions = useMemo<
    EcosystemWalletOptions['connectableOptions']
  >(
    () =>
      filterCosmosWallets({
        wallets: allCosmosWallets,
        type: 'injected',
      }).map((wallet) => {
        const walletOption = makeWalletOption(wallet)
        return {
          ...walletOption,
          connect: (params) => connect({ wallet: walletOption, ...params }),
        }
      }),
    [allCosmosWallets, connect],
  )

  const installableCosmosWallets = useMemo(
    () =>
      filterCosmosWallets({
        wallets: allCosmosWallets,
        type: 'installable',
      }).map(makeWalletOption),
    [allCosmosWallets],
  )

  const connectedWalletOption = useMemo(() => {
    if (!wallet) return undefined
    // Don't show OKX as connected option
    if (wallet.options.pretty_name.includes('OKX')) return undefined
    return {
      ...makeWalletOption(wallet),
      accountName,
      disconnect,
    }
  }, [wallet, disconnect, accountName])

  return useMemo<EcosystemWalletOptions>(
    () => ({
      chainType: ChainType.Cosmos,
      ecosystemName: 'Cosmos',
      ecosystemImgUrl: '/assets/wallets/cosmos.svg',
      connectableOptions,
      connectedWalletOption,
      installableWalletOptions: installableCosmosWallets,
      enabled: featureFlags.cosmosEcosystem,
      ready:
        (isReady || setupStatus === 'INITIALIZED') &&
        (disconnected || rejected || Boolean(accountName)),
    }),
    [
      connectableOptions,
      connectedWalletOption,
      installableCosmosWallets,
      featureFlags.cosmosEcosystem,
      isReady,
      setupStatus,
      disconnected,
      rejected,
      accountName,
    ],
  )
}

const filterCosmosWallets = ({
  wallets,
  type,
}: {
  wallets: ReturnType<typeof useConfig>['wallets']
  type: 'all' | 'installable' | 'injected'
}) => {
  return wallets.filter(({ injected, options: { pretty_name } }) => {
    // Filter out OKX wallets
    if (pretty_name.includes('OKX')) return false

    if (type === 'all') return true
    if (type === 'installable') return !injected
    return injected
  })
}

const useSelectableCosmosWallets = ({
  searchWalletQuery,
}: {
  searchWalletQuery: string
}) => {
  const { wallets } = useConfig()
  const store = useQuirks()
  const router = useRouter()

  useSyncExternalStore(
    () => {
      const listener = store.subscribe(
        (state) => state.accounts,
        (current, prev) => {
          /**
           * Avoid useless route refresh if we don't change cookie store state
           */
          if (JSON.stringify(current) !== JSON.stringify(prev)) {
            router.refresh()
          }
        },
      )

      return () => {
        listener()
      }
    },
    () => {},
    () => {},
  )

  return {
    wallets: wallets.filter(
      ({ options: { pretty_name } }) =>
        !searchWalletQuery ||
        pretty_name.toLowerCase().includes(searchWalletQuery.toLowerCase()),
    ),
  }
}

function makeWalletOption(wallet: Wallet<unknown, unknown>): WalletOption {
  return {
    id: wallet.options.wallet_name,
    displayName: wallet.options.pretty_name.replace(/[^\w\s-]/g, ''),
    // Some wallets return logos with leading/trailing whitespace, need to trim
    // This will cause Next.js Image component to throw downstream
    logoUrl: wallet.logoLight?.trim() ?? '',
    website: wallet.options.website,
  }
}
